import React, { useState } from 'react';
import { ReactSVG } from 'react-svg';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { DEFAULT_CONTENT_WIDTH } from '../../constants';
import { getFullYearNumber } from '@/utils/util_helpers/formatting/dates';
import { SocialIcon } from '@/components/design-system/atoms/icon';
import { theme } from '@/utils/util_theme';
import { COLOC_MAIN_APP } from '@/config';
import { useLocale } from '@/hooks/use-locale';
import { useNewsletterSubscription } from '@/hooks/use-newsletter-subscription';
import { validateEmail, sanitizeEmail } from '@/utils/validation/email';
import { isBrowser } from '@/utils/util_helpers/ssr';

const SOCIAL_LINKS = [
  { name: 'facebook', url: 'https://www.facebook.com/ColocHousing', label: 'Facebook' },
  { name: 'instagram', url: 'https://www.instagram.com/colochousing', label: 'Instagram' },
  { name: 'youtube', url: 'https://www.youtube.com/@colochousing', label: 'YouTube' },
  { name: 'linkedin', url: 'https://www.linkedin.com/company/colochousing/', label: 'LinkedIn' },
] as const;

const Footer = () => {
  const { currentLocale } = useLocale();
  const { t } = useTranslation();
  const { subscribeNewsletter, isLoading } = useNewsletterSubscription();

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = sanitizeEmail(e.target.value);
    setEmail(sanitizedValue);

    if (emailError) {
      setEmailError('');
    }

    if (isSuccess) {
      setIsSuccess(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = validateEmail(email);

    if (!validation.isValid) {
      const errorKey = `js.features.marketplace.newsletter.errors.${validation.error}`;
      setEmailError(t(errorKey));
      return;
    }

    const sanitizedEmail = sanitizeEmail(email);
    setEmailError('');

    try {
      await subscribeNewsletter(sanitizedEmail);

      setIsSuccess(true);
      setEmail('');

      if (isBrowser()) {
        sessionStorage.setItem('subscribed_email', sanitizedEmail);
      }
    } catch (error) {
      const errorMessage = t('js.features.marketplace.newsletter.errors.subscription_failed');
      setEmailError(errorMessage);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(e as unknown as React.FormEvent);
    }
  };

  const exploreLinks = [
    {
      href: `${COLOC_MAIN_APP}/${currentLocale}/shared-houses`,
      label: t('js.features.marketplace.components.footer.explore.houses'),
    },
    {
      href: 'https://help.colochousing.com/',
      label: t('js.features.marketplace.components.footer.explore.help_center'),
    },
    {
      href: `${COLOC_MAIN_APP}/${currentLocale}/terms-and-conditions`,
      label: t('js.features.marketplace.components.footer.explore.terms'),
    },
  ];

  const companyLinks = [
    {
      href: `${COLOC_MAIN_APP}/${currentLocale}/about-us`,
      label: t('js.features.marketplace.components.footer.explore.about_us'),
      turbolinks: false,
    },
    {
      href: `${COLOC_MAIN_APP}/${currentLocale}/press`,
      label: t('js.features.marketplace.components.footer.company.press'),
      turbolinks: false,
    },
    {
      href: 'https://www.linkedin.com/company/colochousing/jobs/',
      label: t('js.features.marketplace.components.footer.company.jobs'),
      external: true,
    },
  ];

  return (
    <footer className={clsx('w-full bg-text-primary', 'text-body text-E75')}>
      <div
        className={clsx(
          DEFAULT_CONTENT_WIDTH,
          'm-auto flex flex-col',
          'gap-7 lg:gap-0',
          'py-7 sm:py-8 lg:py-10'
        )}
      >
        {/* Company Logo - Desktop only */}
        <ReactSVG
          beforeInjection={(svg) => {
            svg.setAttribute('width', '99');
            svg.setAttribute('height', '66');
          }}
          className={clsx('align-baseline', 'hidden lg:inline-block')}
          src={require(`@assets/images/marketplace/footer/coloc-logo.svg`)}
        />

        {/* Main Content Section */}
        <div className={clsx('flex', 'flex-col lg:flex-row', 'gap-7 lg:gap-10', 'lg:mt-8')}>
          {/* Company Info */}
          <div>
            {/* Logo - Mobile only */}
            <div className="mb-5 lg:hidden">
              <ReactSVG
                beforeInjection={(svg) => {
                  svg.setAttribute('width', '99');
                  svg.setAttribute('height', '66');
                }}
                className="inline-block align-baseline"
                src={require(`@assets/images/marketplace/footer/coloc-logo.svg`)}
              />
            </div>

            {/* Contact Info */}
            <div className={clsx('flex flex-col', 'gap-1', 'text-N600')}>
              <a
                href="mailto:info@colochousing.com"
                className={clsx(
                  'transition-colors duration-200',
                  'hover:text-text-primaryAccentDefault'
                )}
              >
                info@colochousing.com
              </a>
              <a
                href="tel:+3226154410"
                className={clsx(
                  'transition-colors duration-200',
                  'hover:text-text-primaryAccentDefault'
                )}
              >
                0032 2 615 44 10
              </a>
              <span>Veldstraat 88 bus A202, 9000 Gent</span>
            </div>

            {/* Business Details */}
            <div className={clsx('flex flex-col', 'gap-1', 'text-N600', 'mt-3')}>
              <span>Alex de Witte</span>
              <span>IPI 500 385</span>
              <span>NV Axa Belgium n°479.232.755</span>
              <span>
                {t('js.features.marketplace.components.footer.account_number', {
                  account: 'BE37 0689 4916 4528',
                })}
              </span>
            </div>
          </div>

          {/* Navigation Columns */}
          <nav className={clsx('flex flex-wrap', 'gap-6')}>
            {/* Explore Column */}
            <div className={clsx('flex flex-col', 'w-[170px]', 'gap-3')}>
              <span className={clsx('font-bold text-white', 'text-lead2')}>
                {t('js.features.marketplace.components.footer.explore.title')}
              </span>
              {exploreLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  className={clsx(
                    'text-N600 transition-colors duration-200',
                    'hover:text-text-primaryAccentDefault'
                  )}
                >
                  {link.label}
                </a>
              ))}
            </div>

            {/* Company Column */}
            <div className={clsx('flex flex-col', 'w-[170px]', 'gap-3')}>
              <span className={clsx('font-bold text-white', 'text-lead2')}>
                {t('js.features.marketplace.components.footer.company.title')}
              </span>
              {companyLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  className={clsx(
                    'text-N600 transition-colors duration-200',
                    'hover:text-text-primaryAccentDefault'
                  )}
                  {...(link.turbolinks === false && { 'data-turbolinks': 'false' })}
                  {...(link.external && { target: '_blank', rel: 'noreferrer noopener' })}
                >
                  {link.label}
                </a>
              ))}
            </div>

            {/* Newsletter Column */}
            <div className={clsx('flex flex-col', 'w-full lg:w-auto lg:min-w-[300px]', 'gap-3')}>
              <span className={clsx('font-bold text-white', 'text-lead2')}>
                {t('js.features.marketplace.components.footer.newsletter.title')}
              </span>
              <form onSubmit={handleSubmit}>
                <div className={clsx('flex flex-col', 'gap-2')}>
                  <div className={clsx('flex flex-row items-center', 'gap-2 sm:gap-3')}>
                    <input
                      type="email"
                      placeholder={t(
                        'js.features.marketplace.components.footer.newsletter.placeholder'
                      )}
                      value={email}
                      onChange={handleEmailChange}
                      onKeyDown={handleKeyDown}
                      disabled={isLoading || isSuccess}
                      className={clsx(
                        'min-w-0 flex-1 rounded bg-bg-secondaryMedium focus:outline-none focus:ring-1',
                        'px-3 sm:px-4',
                        'py-3',
                        'text-body text-E75 placeholder:text-E100',
                        emailError
                          ? 'border-red-500 focus:ring-red-500 border-2'
                          : isSuccess
                          ? 'border-1 border-green-500 focus:ring-[#ededf0]'
                          : 'focus:ring-[#ededf0]',
                        'disabled:cursor-not-allowed disabled:opacity-60'
                      )}
                      aria-label={t(
                        'js.features.marketplace.components.footer.newsletter.placeholder'
                      )}
                      aria-invalid={!!emailError}
                      aria-describedby={emailError ? 'footer-email-error' : undefined}
                      maxLength={254}
                      autoComplete="email"
                    />
                    <button
                      type="submit"
                      disabled={isLoading || !email || isSuccess}
                      className={clsx(
                        'flex-shrink-0 whitespace-nowrap rounded transition-all duration-200',
                        'px-3 sm:px-4',
                        'py-3',
                        'font-bold text-text-primary',
                        'text-body',
                        'bg-bg-primaryAccentHeavy',
                        'shadow-[0_5px_30px_0_rgba(254,206,31,0.24)]',
                        'hover:bg-fill-button-primaryMedium',
                        'active:bg-text-yellow',
                        'disabled:cursor-not-allowed disabled:bg-fill-button-disabled disabled:opacity-60 disabled:shadow-none'
                      )}
                      aria-label={t('js.features.marketplace.components.footer.newsletter.button')}
                    >
                      {isLoading
                        ? t('js.features.marketplace.newsletter.button_subscribing')
                        : isSuccess
                        ? t('js.features.marketplace.newsletter.button_subscribed')
                        : t('js.features.marketplace.components.footer.newsletter.button')}
                    </button>
                  </div>
                  {emailError && (
                    <p id="footer-email-error" className={clsx('text-red-400', 'text-sm')}>
                      {emailError}
                    </p>
                  )}
                  {isSuccess && (
                    <p className={clsx('text-green-400', 'text-sm')}>
                      {t('js.features.marketplace.newsletter.success')}
                    </p>
                  )}
                </div>
              </form>
            </div>
          </nav>
        </div>

        {/* Divider Line */}
        <div className={clsx('bg-E75 opacity-10', 'h-[1px] w-full', 'mt-5 lg:mt-5')} />

        {/* Bottom Section - Social & Trademark */}
        <div
          className={clsx(
            'flex justify-between',
            'flex-col-reverse lg:flex-row',
            'w-full',
            'gap-5',
            'mt-5'
          )}
        >
          {/* Trademark */}
          <span className={clsx('text-N600', 'text-center')}>
            {`©${getFullYearNumber(new Date())} COSY BV • VAT BE0773.923.606`}
          </span>

          {/* Social Icons */}
          <div className={clsx('flex flex-wrap items-center justify-center', 'gap-4')}>
            {SOCIAL_LINKS.map((social) => (
              <a
                className='p-2 pb-1 hover:bg-bg-secondaryMedium rounded-full transition-colors duration-200'
                key={social.name}
                href={social.url}
                target="_blank"
                rel="noreferrer noopener"
                aria-label={`Visit us on ${social.label}`}
              >
                <SocialIcon
                  variant="black-white"
                  name={social.name as any}
                  size={window.innerWidth >= 1024 ? 20 : 24}
                  strokeColor={theme?.colors.secondary.primaryHeavy}
                  hoverStrokeColor={theme?.colors.text.primaryAccentDefault}
                />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export { Footer };
